import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { CalendarDays, DatabaseZap, Keyboard, Plug, Settings2, SunMoon, Users, Zap } from "lucide-react";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { ProofIconBold } from "@/components/codesmith/proof/proofIcon";
import { type SettingsSection, settingsPath, settingsRouteFromPathname } from "@/lib/settingsRoutes";
import { cn } from "@repo/blacksmith-ui/cn";
import { RoutePrefetchLink } from "../ui/route-prefetch-link";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "../ui/sidebar";
import { AppSidebarHeader, AppSidebarHeaderActions } from "../ui/app-header";
import { useCodesmithSidebarVariant } from "@/components/codesmith/useCachedSidebarRedesignEnabled";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
const menuItemClassName = "group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1";
const menuButtonClassName = "h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 data-[active=true]:bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:data-[active=true]:bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white cursor-pointer";
type SectionNavItem = {
  name: string;
  section: SettingsSection;
  icon: React.ReactNode;
};

/**
 * Contextual sidebar for the settings routes. Sits to the right of the
 * app-switcher rail and links to the section pages under
 * `/[org]/settings`. Only the section page swaps on navigation; the settings
 * layout stays mounted.
 */
export default function SettingsSidebar() {
  const $ = _c(60);
  const router = useRouter();
  const {
    activeGithubOrg,
    codesmithEnabled,
    codesmithProofSettingsEnabled
  } = useActiveGithubOrgForUserNonSuspense();
  const desktop = useCodesmithSurface(router.query.embed) === "desktop";
  const urlOrg = router.query.org;
  const currentOrg = typeof urlOrg === "string" ? urlOrg : activeGithubOrg;
  const redesign = useCodesmithSidebarVariant(currentOrg ?? null) === "redesign";
  const t0 = redesign ? "gap-0" : "gap-1";
  let t1;
  if ($[0] !== t0) {
    t1 = cn("flex flex-col", t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const menuClassName = t1;
  const t2 = redesign && "group-data-[state=expanded]:border-y-2 group-data-[state=expanded]:bg-clip-padding";
  let t3;
  if ($[2] !== t2) {
    t3 = cn(menuButtonClassName, t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const buttonClassName = t3;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t10;
  let t11;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== buttonClassName || $[5] !== codesmithEnabled || $[6] !== codesmithProofSettingsEnabled || $[7] !== currentOrg || $[8] !== desktop || $[9] !== menuClassName || $[10] !== redesign || $[11] !== router.pathname) {
    const codesmithNav = codesmithEnabled ? [{
      name: "[code]smith",
      section: "codesmith",
      icon: <CodesmithIcon className="size-[18px]" />
    }, ...(codesmithProofSettingsEnabled ? [{
      name: "Proof",
      section: "proof" as const,
      icon: <ProofIconBold className="size-[18px]" />
    }] : [])] : [];
    const nav = desktop ? codesmithNav : [{
      name: "General",
      section: "general",
      icon: <Settings2 className="size-[18px]" />
    }, {
      name: "Appearance",
      section: "appearance",
      icon: <SunMoon className="size-[18px]" />
    }, {
      name: "Features",
      section: "features",
      icon: <Zap className="size-[18px]" />
    }, {
      name: "Caching",
      section: "caching",
      icon: <DatabaseZap className="size-[18px]" />
    }, {
      name: "Integrations",
      section: "integrations",
      icon: <Plug className="size-[18px]" />
    }, {
      name: "Team",
      section: "team",
      icon: <Users className="size-[18px]" />
    }, ...codesmithNav];
    const activeSection = settingsRouteFromPathname(router.pathname)?.section ?? "general";
    T4 = ShadcnSidebar;
    t9 = "icon";
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = cn("left-[var(--app-switcher-rail-width,_48px)] transition-[left] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]", "[&_[data-slot=sidebar-inner]]:border-[#0c0c0d14] dark:[&_[data-slot=sidebar-inner]]:border-white/12");
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <SidebarHeader className="shrink-0 bg-sidebar p-0"><AppSidebarHeader title="Settings" actions={<AppSidebarHeaderActions />} collapsible={true} /></SidebarHeader>;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    T3 = SidebarContent;
    t8 = "flex min-h-[200px] flex-col gap-0 overflow-hidden bg-sidebar py-0 group-data-[state=collapsed]:px-0 group-data-[collapsible=icon]:overflow-hidden";
    T2 = SidebarGroup;
    const t12 = redesign ? "pt-0" : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
    if ($[27] !== t12) {
      t7 = cn("border-0 min-h-0 flex-1 overflow-y-auto group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1", t12);
      $[27] = t12;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    T1 = SidebarGroupContent;
    t6 = "flex flex-1 flex-col px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0";
    T0 = SidebarMenu;
    t4 = menuClassName;
    t5 = nav.map(item => {
      const href = settingsPath(currentOrg, item.section);
      return <SidebarMenuItem key={item.name} className={menuItemClassName}><SidebarMenuButton asChild={true} isActive={activeSection === item.section} tooltip={item.name} className={buttonClassName}><RoutePrefetchLink href={href} prefetch={currentOrg ? true : null} prefetchOnIntent={!!currentOrg} className="cursor-pointer"><div className="relative">{item.icon}</div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{item.name}</span></RoutePrefetchLink></SidebarMenuButton></SidebarMenuItem>;
    });
    $[4] = buttonClassName;
    $[5] = codesmithEnabled;
    $[6] = codesmithProofSettingsEnabled;
    $[7] = currentOrg;
    $[8] = desktop;
    $[9] = menuClassName;
    $[10] = redesign;
    $[11] = router.pathname;
    $[12] = T0;
    $[13] = T1;
    $[14] = T2;
    $[15] = T3;
    $[16] = T4;
    $[17] = t10;
    $[18] = t11;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[12];
    T1 = $[13];
    T2 = $[14];
    T3 = $[15];
    T4 = $[16];
    t10 = $[17];
    t11 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t12;
  if ($[29] !== T0 || $[30] !== t4 || $[31] !== t5) {
    t12 = <T0 className={t4}>{t5}</T0>;
    $[29] = T0;
    $[30] = t4;
    $[31] = t5;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== T1 || $[34] !== t12 || $[35] !== t6) {
    t13 = <T1 className={t6}>{t12}</T1>;
    $[33] = T1;
    $[34] = t12;
    $[35] = t6;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== T2 || $[38] !== t13 || $[39] !== t7) {
    t14 = <T2 className={t7}>{t13}</T2>;
    $[37] = T2;
    $[38] = t13;
    $[39] = t7;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== T3 || $[42] !== t14 || $[43] !== t8) {
    t15 = <T3 className={t8}>{t14}</T3>;
    $[41] = T3;
    $[42] = t14;
    $[43] = t8;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== buttonClassName || $[46] !== desktop) {
    t16 = !desktop && <SidebarMenuItem className={menuItemClassName}><SidebarMenuButton asChild={true} tooltip="Book a demo" className={buttonClassName}><a href="https://www.blacksmith.sh/contact-our-team" target="_blank" rel="noopener noreferrer"><div className="relative"><CalendarDays className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Book a demo</span></a></SidebarMenuButton></SidebarMenuItem>;
    $[45] = buttonClassName;
    $[46] = desktop;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SidebarMenuItem className={menuItemClassName}><SidebarMenuButton tooltip="Keyboard shortcuts" className={menuButtonClassName} onClick={_temp}><div className="relative"><Keyboard className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Keyboard shortcuts</span></SidebarMenuButton></SidebarMenuItem>;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== menuClassName || $[50] !== t16) {
    t18 = <SidebarFooter className="shrink-0 gap-0 p-0 group-data-[state=collapsed]:border-t-0"><SidebarGroup className="border-0 py-2 group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1"><SidebarGroupContent className="px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0"><SidebarMenu className={menuClassName}>{t16}{t17}</SidebarMenu></SidebarGroupContent></SidebarGroup></SidebarFooter>;
    $[49] = menuClassName;
    $[50] = t16;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <SidebarRail />;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== T4 || $[54] !== t10 || $[55] !== t11 || $[56] !== t15 || $[57] !== t18 || $[58] !== t9) {
    t20 = <T4 collapsible={t9} className={t10}>{t11}{t15}{t18}{t19}</T4>;
    $[53] = T4;
    $[54] = t10;
    $[55] = t11;
    $[56] = t15;
    $[57] = t18;
    $[58] = t9;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  return t20;
}
function _temp() {
  return document.dispatchEvent(new KeyboardEvent("keydown", {
    key: "?",
    bubbles: true,
    cancelable: true
  }));
}